// Finance system — docs/ARCHITECTURE_M0.md §5.8, M2 rev (ARCHITECTURE_M2.md §3).
// The M0 closing-hazard model is kept, but a "close" now produces a TERM SHEET
// the player must accept (real dilution math on a shares ledger) instead of
// auto-closing. Also: opex (+ public-company costs), runway, stage gates,
// bankruptcy, and the end-of-tick close that threads street + board.

import { DISCIPLINES } from "../types.js";
import type { TermSheet } from "../types.js";
import type { TickContext } from "../ctx.js";
import { charge, emit } from "../ctx.js";
import { interpolate } from "../calendar.js";
import { salaryOf } from "../commands.js";
import { totalHeadcount } from "./people.js";
import { clamp } from "../prng.js";
import { processStreet } from "./street.js";
import { processBoard } from "./board.js";
import { processHistory } from "./history.js";
import { processStakeholdersClose } from "./stakeholders.js";
import { moraleHit, processTalentClose } from "./talent.js";
import { toolCostMult } from "./tools.js";
import { processMaClose } from "./ma.js";

/** Fully diluted shares outstanding. */
export function totalShares(fin: {
  founderShares: number;
  investorShares: number;
  poolShares: number;
  publicShares: number;
}): number {
  return fin.founderShares + fin.investorShares + fin.poolShares + fin.publicShares;
}

/** Recompute the derived founderShare after any ledger change. */
export function refreshFounderShare(ctx: TickContext): void {
  const fin = ctx.s.finance;
  fin.founderShare = fin.founderShares / totalShares(fin);
}

/** TTM net income over completed months: revenue (incl. NRE) − COGS − opex.
 *  The earnings leg of the street's fair-value story (and private M&A pricing). */
export function ttmEarnings(kpiHistory: { revenue: number; cogs: number; opex: number }[]): number {
  let sum = 0;
  for (let i = Math.max(0, kpiHistory.length - 12); i < kpiHistory.length; i++) {
    const k = kpiHistory[i]!;
    sum += k.revenue - k.cogs - k.opex;
  }
  return sum;
}

const ROUND_NAMES = ["Seed", "Series A", "Series B", "Series C", "Series D", "Series E"];

export function roundName(n: number): string {
  return ROUND_NAMES[n - 1] ?? `Series ${String.fromCharCode(64 + n - 1)}`;
}

export function processFinancing(ctx: TickContext): void {
  const { s, content } = ctx;
  const fin = s.finance;
  const cfg = content.economy.financing;
  const sheets = content.economy.termSheets;

  if (fin.raiseCooldownMonths > 0) {
    fin.raiseCooldownMonths = Math.max(0, fin.raiseCooldownMonths - ctx.monthsPerTick);
  }
  if (fin.secondaryCooldownMonths > 0) {
    fin.secondaryCooldownMonths = Math.max(0, fin.secondaryCooldownMonths - ctx.monthsPerTick);
  }

  // an open sheet ages toward expiry; the hazard process pauses while it's open
  if (fin.openTermSheet) {
    fin.openTermSheet.monthsLeft -= ctx.monthsPerTick;
    if (fin.openTermSheet.monthsLeft <= 0) {
      fin.openTermSheet = null;
      emit(ctx, "termSheetExpired", "The term sheet expired unanswered — the lead moved on.", {});
    }
    return;
  }
  if (!fin.activeRaise) return;
  fin.activeRaise.monthsActive += ctx.monthsPerTick;

  const ttmRev = fin.ttmRevenue.reduce((a, b) => a + b, 0);
  // a tree position IS traction (P0, TUNING_P0 §3a): investors in '94 funded
  // architecture — a company holding real researched capability reads as more
  // fundable pre-silicon than a deck. (Zero researched nodes for legacy
  // scenarios, so this tier never fires there.)
  const treePosition = Object.keys(s.tree.researched).length >= cfg.readinessTreeNodes;
  const readinessBase =
    ttmRev > 10_000_000
      ? cfg.readinessRevenue10M
      : ttmRev > 2_000_000
        ? cfg.readinessRevenue2M
        : fin.everWorkingSilicon
          ? cfg.readinessWorkingSilicon
          : treePosition
            ? cfg.readinessTreePosition
            : cfg.readinessPreSilicon;
  // story fatigue: "we have working silicon" was fundable in '95 — not on the
  // fifth round with years-old silicon and no traction. The living dead die.
  const fatigued = fin.roundsCompleted >= cfg.storyFatigueRounds && ttmRev < cfg.storyFatigueTtm;
  const readiness = fatigued ? readinessBase * cfg.storyFatigueFactor : readinessBase;
  const sentiment = interpolate(content.economy.sentiment, s.monthIndex);
  const hazard = clamp(cfg.pBase + cfg.pSlope * sentiment * readiness, cfg.pMin, cfg.pMax);

  const eraScale = interpolate(content.economy.eraCapitalScale, s.monthIndex);
  const cap = (2 + 18 * readiness) * 1e6 * sentiment * eraScale;
  const amount = Math.round(Math.min(fin.activeRaise.ask, cap));
  // a $0 offer is no offer at all (guards NaN dilution if content ever zeroes eraCapitalScale)
  if (amount > 0 && ctx.rng.bernoulli(hazard * ctx.monthsPerTick)) {
    // a lead investor commits — draw up the sheet. Terms price in the era, the
    // company's traction, and how the board room feels about management.
    const conf = s.board.confidence;
    const confFactor =
      conf >= sheets.confHighFloor
        ? sheets.confHighBonus
        : conf < sheets.confCrisisFloor
          ? sheets.confCrisisHaircut
          : conf < sheets.confLowFloor
            ? sheets.confLowHaircut
            : 1;
    const sigma = sheets.valuationSigma;
    const noise = ctx.rng.logNormal(-(sigma * sigma) / 2, sigma);
    const preMoney = Math.round(
      Math.max(2.5 * fin.totalRaised, 4 * ttmRev, 2.5e6 * eraScale) * (0.6 + 0.8 * sentiment) * confFactor * noise,
    );
    const sheet: TermSheet = {
      round: fin.roundsCompleted + 1,
      amount,
      preMoney,
      poolTopUpPct: fin.roundsCompleted < sheets.earlyRounds ? sheets.poolTopUpEarly : sheets.poolTopUpLate,
      boardSeat: amount >= sheets.boardSeatMinAmount,
      downRound: fin.lastPostMoney > 0 && preMoney < fin.lastPostMoney,
      monthsLeft: sheets.lifeMonths,
    };
    fin.openTermSheet = sheet;
    emit(
      ctx,
      "termSheet",
      `Term sheet: $${(amount / 1e6).toFixed(1)}M at $${(preMoney / 1e6).toFixed(1)}M pre` +
        `${sheet.downRound ? " — a DOWN round" : ""}${sheet.boardSeat ? ", board seat" : ""}. Expires in ${sheets.lifeMonths} months.`,
      {
        round: sheet.round,
        amount,
        preMoney,
        poolTopUpPct: sheet.poolTopUpPct,
        boardSeat: sheet.boardSeat,
        downRound: sheet.downRound,
      },
    );
  } else if (fin.activeRaise.monthsActive >= cfg.maxMonths) {
    fin.activeRaise = null;
    fin.raiseCooldownMonths = cfg.cooldownFailMonths;
    emit(ctx, "roundFailed", "Round withdrawn — no lead investor after months of meetings", { hazard });
  }
}

/** Close the round on an accepted sheet: real cap-table math on the shares ledger.
 *  Pool top-up dilutes existing holders first (investors demand a full pool),
 *  then the new money buys in at preMoney / pre-round fully-diluted. */
export function closeRound(ctx: TickContext, sheet: TermSheet): void {
  const { s } = ctx;
  const fin = s.finance;

  const pre = totalShares(fin);
  const pps = sheet.preMoney / pre;
  const investorNew = Math.round(sheet.amount / pps);
  // top the pool up to poolTopUpPct of post-round fully diluted (counting what's left in it)
  const poolAdd = Math.max(
    0,
    Math.round((sheet.poolTopUpPct * (pre + investorNew) - fin.poolShares) / (1 - sheet.poolTopUpPct)),
  );
  fin.poolShares += poolAdd;
  fin.investorShares += investorNew;
  refreshFounderShare(ctx);

  fin.totalRaised += sheet.amount;
  fin.roundsCompleted += 1;
  fin.lastPostMoney = sheet.preMoney + sheet.amount;
  fin.activeRaise = null;
  fin.openTermSheet = null;
  fin.raiseCooldownMonths = ctx.content.economy.financing.cooldownSuccessMonths;
  fin.rounds.push({
    name: roundName(fin.roundsCompleted),
    month: s.monthIndex,
    amount: sheet.amount,
    preMoney: sheet.preMoney,
    postMoney: fin.lastPostMoney,
    sharesIssued: investorNew,
    poolSharesAdded: poolAdd,
    boardSeat: sheet.boardSeat,
    downRound: sheet.downRound,
  });
  if (sheet.boardSeat) s.board.investorSeats += 1;
  // a down round tells every option holder what their paper is worth (M3 §9)
  if (sheet.downRound) moraleHit(ctx, -ctx.content.economy.talent.downRoundMoraleHit);

  charge(ctx, "financing", sheet.amount, `${roundName(fin.roundsCompleted)} closed`);
  ctx.financingInflow += sheet.amount;
  emit(
    ctx,
    "roundClosed",
    `${roundName(fin.roundsCompleted)} closed: $${(sheet.amount / 1e6).toFixed(1)}M at ` +
      `$${(sheet.preMoney / 1e6).toFixed(1)}M pre (founder share now ${(fin.founderShare * 100).toFixed(1)}%)` +
      `${sheet.downRound ? " — down round" : ""}`,
    {
      amount: sheet.amount,
      preMoney: sheet.preMoney,
      founderShare: fin.founderShare,
      downRound: sheet.downRound,
      boardSeat: sheet.boardSeat,
      round: fin.roundsCompleted,
    },
  );
}

export function processOpex(ctx: TickContext): void {
  const { s, content } = ctx;
  const eco = content.economy;

  // comp policy (M3, design §9): pay above band and everyone stays; below band
  // is cheap until the labor market heats up and the poachers call
  const compMult = 1 + eco.talent.compPayrollSpan * s.talent.compPolicy;

  let payroll = 0;
  let tools = 0;
  let heads = 0;
  const toolRate = interpolate(eco.toolCostPerEngYear, s.monthIndex) / 12;
  for (const d of DISCIPLINES) {
    const pool = s.people.pools[d];
    const count = pool.productive + pool.ramping.reduce((a, r) => a + r.count, 0);
    heads += count;
    payroll += count * (salaryOf(ctx, d) / 12) * eco.overheadBurden * compMult * ctx.monthsPerTick;
    // seat costs by discipline: the EDA tier scales the whole line; a working
    // in-house flow cuts that discipline's renewals (M3, design §6.3)
    tools += count * toolRate * toolCostMult(ctx, d) * ctx.monthsPerTick;
  }
  // stars are paid far above band — that's what a name costs (M3, design §9)
  for (const star of s.talent.stars) {
    payroll += (salaryOf(ctx, star.discipline) / 12) * star.salaryMult * eco.overheadBurden * compMult * ctx.monthsPerTick;
  }
  const gna = (interpolate(eco.gnaBaseMonthly, s.monthIndex) + eco.gnaPerHeadMonthly * heads) * ctx.monthsPerTick;

  charge(ctx, "payroll", -payroll, `payroll ${heads} heads`);
  charge(ctx, "tools", -tools, "EDA/tools");
  charge(ctx, "gna", -gna, "G&A");
  // negate-then-round so the KPI matches the posted ledger amounts exactly
  ctx.opex += -Math.round(-payroll) - Math.round(-tools) - Math.round(-gna);

  // being public has a rent: listing, IR, audit — and a step change at SOX (2002)
  if (s.stage === "public") {
    const pubCost = interpolate(eco.street.publicCostMonthly, s.monthIndex) * ctx.monthsPerTick;
    charge(ctx, "publicCompany", -pubCost, "public-company costs (listing/IR/audit/SOX)");
    ctx.opex += -Math.round(-pubCost);
  }
}

export function closeTick(ctx: TickContext): void {
  const { s } = ctx;
  const fin = s.finance;

  // a mid-tick exit (accepted acquisition, or folding in the garage) ends the
  // campaign cleanly: no meters, meetings, or news — just advance the clock
  if (s.dead && (s.deathReason === "acquired" || s.deathReason === "folded")) {
    s.tick += 1;
    s.monthIndex += ctx.monthsPerTick;
    return;
  }

  fin.ttmRevenue.push(ctx.revenue);
  while (fin.ttmRevenue.length > 12) fin.ttmRevenue.shift();

  // stage gate angel → vc (design §3 Stage 1 exit): an institutional round on
  // the books plus working silicon or real traction. Checked monthly (not just
  // at round close) so a company that grows between raises graduates on merit.
  if (s.stage === "angel" && fin.roundsCompleted >= 1) {
    const ttmRev = fin.ttmRevenue.reduce((a, b) => a + b, 0);
    if (fin.everWorkingSilicon || ttmRev > 2_000_000) {
      s.stage = "vc";
      emit(ctx, "stageAdvanced", "The company is now VC-backed. The board expects milestones.", {
        stage: "vc",
        round: fin.roundsCompleted,
      });
    }
  }

  // stage gate vc → growth (design §3 Stage 2 exit): production revenue at
  // positive gross margin plus at least 2 active design wins
  if (s.stage === "vc") {
    const recent = s.kpiHistory.slice(-2);
    const qRev = recent.reduce((a, k) => a + k.revenue - k.nreRevenue, 0) + ctx.revenue;
    const qCogs = recent.reduce((a, k) => a + k.cogs, 0) + ctx.cogs;
    const activeWins = s.products.filter((p) => p.active).length;
    if (qRev > 0 && qRev > qCogs && activeWins >= 2) {
      s.stage = "growth";
      emit(
        ctx,
        "stageAdvanced",
        "Production revenue at positive gross margin with two design wins — the company enters its growth stage. Underwriters are starting to call.",
        { stage: "growth" },
      );
    }
  }

  // public-market layer first (may emit earnings events)…
  processStreet(ctx);
  // …then the board reads everything that happened this tick
  processBoard(ctx);
  // stakeholder meters, penalties, and this month's asks (M3, design §11)…
  processStakeholdersClose(ctx);
  // …the talent market reacts to the month (morale, poachers, offers)…
  processTalentClose(ctx);
  // …acquirers circle when you're cheap (M3, both directions)
  processMaClose(ctx);
  // historical rail cards for the month being resolved
  processHistory(ctx);

  const cashDelta = s.cash - ctx.cashAtTickStart;
  const burn = Math.max(0, -(cashDelta - ctx.financingInflow));
  fin.burnHistory.push(burn);
  while (fin.burnHistory.length > 3) fin.burnHistory.shift();
  const avgBurn = fin.burnHistory.reduce((a, b) => a + b, 0) / fin.burnHistory.length;
  const runway = avgBurn > 0 ? Math.min(999, s.cash / avgBurn) : 999;

  // stage stability floor (design §3: ≥3 months angel, ≥6 after) — edge-triggered
  // between-meetings alarm; the board meeting applies the real consequences
  const floor = s.stage === "angel" ? 3 : 6;
  const prevRunway = s.kpiHistory.length > 0 ? s.kpiHistory[s.kpiHistory.length - 1]!.runwayMonths : 999;
  if (runway < floor && prevRunway >= floor && !s.dead) {
    emit(
      ctx,
      "boardConcern",
      s.stage === "angel"
        ? "Runway under 3 months — your angels are asking hard questions."
        : "Runway under 6 months with no term sheet — the board is alarmed.",
      { runway: Math.round(runway * 10) / 10, floor },
    );
  }

  const marketIds = Object.keys(s.markets).sort();
  const share = marketIds.reduce((a, id) => a + s.markets[id]!.totalShare, 0);
  const shareByMarket = Object.fromEntries(marketIds.map((id) => [id, s.markets[id]!.totalShare]));
  const headcountByDiscipline = Object.fromEntries(
    DISCIPLINES.map((d) => [d, s.people.pools[d].productive + s.people.pools[d].ramping.reduce((a, r) => a + r.count, 0)]),
  ) as Record<(typeof DISCIPLINES)[number], number>;

  s.kpiHistory.push({
    month: s.monthIndex,
    cash: s.cash,
    revenue: ctx.revenue + ctx.nreRevenue,
    nreRevenue: ctx.nreRevenue,
    cogs: ctx.cogs,
    opex: ctx.opex,
    headcount: totalHeadcount(ctx),
    share,
    runwayMonths: Math.round(runway * 10) / 10,
    activeProjects: s.projects.filter((p) => p.phase !== "shipped" && p.phase !== "cancelled").length,
    shippedProducts: s.products.length,
    shareByMarket,
    headcountByDiscipline,
  });

  if (s.cash < 0 && !s.dead) {
    s.dead = true;
    s.deathMonth = s.monthIndex;
    s.deathReason = "bankrupt";
    emit(ctx, "bankrupt", "Out of cash. The company is dead.", { month: s.monthIndex });
  }

  s.tick += 1;
  s.monthIndex += ctx.monthsPerTick;
}
